import React, { useMemo } from 'react';
import { NodeProps, NodeResizer } from 'reactflow';
import type { BoardFloorPlanData } from '../../../types';
import { useBoardActions } from '../boardActions';
import FloorPlanDrawing from '../floorplan/FloorPlanDrawing';
import { contentBounds, formatInchesFtIn, underlayIsHidden, underlayRenderStyles } from '../floorplan/floorPlanMath';
import { useBoardMultiSelected } from '../useBoardSelection';
import NodeActionsToolbar from './NodeActionsToolbar';
import './FloorPlanNode.css';

const FloorPlanNode: React.FC<NodeProps<BoardFloorPlanData>> = ({ id, data, selected }) => {
  const { readOnly, editFloorPlan } = useBoardActions();
  const multiSelected = useBoardMultiSelected();
  const pad = 28;
  const bounds = useMemo(() => contentBounds(data), [data]);
  const previewBox = useMemo(() => {
    if (bounds) {
      return {
        x: bounds.minX - pad,
        y: bounds.minY - pad,
        w: bounds.widthIn + pad * 2,
        h: bounds.depthIn + pad * 2,
      };
    }
    const w = data.footprint?.widthIn || 432;
    const d = data.footprint?.depthIn || 288;
    return { x: -pad, y: -pad, w: w + pad * 2, h: d + pad * 2 };
  }, [bounds, data.footprint]);
  const viewBox = `${previewBox.x} ${previewBox.y} ${previewBox.w} ${previewBox.h}`;

  const hasContent =
    (data.walls?.length || 0) > 0 ||
    (data.items?.length || 0) > 0 ||
    (data.openings?.length || 0) > 0 ||
    (data.underlays || []).some((underlay) => !underlayIsHidden(underlay));

  return (
    <>
      <NodeResizer
        isVisible={!!selected && !readOnly && !multiSelected}
        minWidth={280}
        minHeight={200}
        lineClassName="board-resize-line"
        handleClassName="board-resize-handle"
      />
      <NodeActionsToolbar
        id={id}
        showEdit={!readOnly}
        onEdit={() => editFloorPlan(id)}
      />
      <div
        className={`board-node board-floorplan-node ${selected ? 'is-selected' : ''}`}
        onDoubleClick={() => {
          if (!readOnly) editFloorPlan(id);
        }}
      >
        <div className="board-floorplan-head">
          <span className="board-floorplan-title">{data.title || 'Floor plan'}</span>
          <span className="board-floorplan-size">
            {bounds
              ? `${formatInchesFtIn(bounds.widthIn)} × ${formatInchesFtIn(bounds.depthIn)}`
              : 'Draw walls'}
          </span>
        </div>
        <div className="board-floorplan-preview">
          {hasContent ? (
            <div
              className="board-floorplan-preview-stage"
              style={{
                ['--fp-ar' as string]: String(previewBox.w / Math.max(previewBox.h, 1)),
              }}
            >
              <div className="board-floorplan-underlays">
                {(data.underlays || []).filter((underlay) => !underlayIsHidden(underlay)).map((underlay) => {
                  const styles = underlayRenderStyles(underlay, previewBox);
                  return (
                    <div key={underlay.id} className="board-floorplan-underlay-clip" style={styles.clip}>
                      <img
                        className="board-floorplan-underlay"
                        src={underlay.url}
                        alt=""
                        draggable={false}
                        style={styles.image}
                      />
                    </div>
                  );
                })}
              </div>
              <svg
                viewBox={viewBox}
                preserveAspectRatio="xMidYMid meet"
                className="board-floorplan-svg"
                aria-hidden
              >
                <FloorPlanDrawing data={data} showGrid={false} showDimensions={false} />
              </svg>
            </div>
          ) : (
            <div className="board-floorplan-empty">Double-click to draw the floor plan</div>
          )}
        </div>
      </div>
    </>
  );
};

export default FloorPlanNode;
